import React, { useState } from 'react';
import LegalPageShell from './components/LegalPageShell';
import PageSeo from './seo/PageSeo';
import useFormSubmitGuard from './hooks/useFormSubmitGuard';
import {
  formatCpf,
  isValidCpf,
  isValidEmail,
} from './utils/patientContactValidation';

const BASE_URL = (process.env.REACT_APP_API_URL || window.location.origin).replace(/\/+$/, '');

const stripPhoneDigits = (raw) => {
  let d = String(raw || '').replace(/\D/g, '');
  while (d.length > 11 && d.startsWith('55')) d = d.slice(2);
  if (d.startsWith('0') && (d.length === 11 || d.length === 12)) d = d.slice(1);
  return d.slice(0, 11);
};

const formatPhoneBR = (raw) => {
  const digits = stripPhoneDigits(raw);
  if (digits.length <= 2) return digits.length ? `(${digits}` : '';
  if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
};

const fieldStyle = {
  display: 'block',
  width: '100%',
  boxSizing: 'border-box',
  marginTop: '0.35rem',
  padding: '0.75rem 0.85rem',
  borderRadius: '0.65rem',
  border: '1px solid rgba(0,0,0,0.12)',
  fontSize: '0.95rem',
  fontFamily: 'inherit',
};

const labelStyle = {
  display: 'block',
  fontSize: '0.88rem',
  fontWeight: 600,
  color: '#374151',
  marginBottom: '0.85rem',
};

const btnStyle = (disabled) => ({
  marginTop: '1.25rem',
  width: '100%',
  padding: '0.85rem 1rem',
  borderRadius: '0.75rem',
  border: 'none',
  background: disabled ? '#9CA3AF' : '#D32F2F',
  color: '#fff',
  fontWeight: 700,
  fontSize: '0.95rem',
  cursor: disabled ? 'not-allowed' : 'pointer',
});

export default function DadosExclusao() {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [cpf, setCpf] = useState('');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');
  const [protocol, setProtocol] = useState('');
  const [done, setDone] = useState(false);

  const formValid =
    fullName.trim().split(/\s+/).filter((t) => t.length >= 2).length >= 2
    && isValidEmail(email)
    && isValidCpf(cpf)
    && stripPhoneDigits(phone).length >= 10;

  const handleSubmitRaw = async (e) => {
    e.preventDefault();
    if (!formValid) return false;
    setError('');

    try {
      const res = await fetch(`${BASE_URL}/patient/lgpd-self-delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fullName: fullName.trim(),
          email: email.trim().toLowerCase(),
          cpf: cpf.replace(/\D/g, ''),
          phone: stripPhoneDigits(phone),
        }),
      });
      const data = await res.json().catch(() => ({}));

      if (res.status === 429) {
        setError('Muitas tentativas em pouco tempo. Aguarde alguns minutos e tente novamente.');
        return false;
      }

      if (res.ok && data.ok) {
        setProtocol(String(data.protocol || ''));
        setDone(true);
        return true;
      }

      if (data.error === 'invalid_input') {
        setError('Revise os dados informados. Confira nome completo, e-mail, CPF e telefone com DDD.');
        return false;
      }

      setError(
        'Não encontramos cadastro com esses quatro dados juntos. '
        + 'Revise nome completo, e-mail, CPF e telefone (com DDD) exatamente como usou na consulta.'
      );
      return false;
    } catch {
      setError('Falha de conexão. Verifique sua internet e tente novamente.');
      return false;
    }
  };

  const [submitting, handleSubmit] = useFormSubmitGuard(handleSubmitRaw, { cooldownMs: 4000 });

  if (done) {
    return (
      <>
        <PageSeo
          path="/dados-exclusao"
          title="Exclusão de dados — Consulta+"
          description="Solicite a exclusão dos seus dados cadastrais na Consulta+, conforme a LGPD."
          breadcrumbs={[
            { name: 'Início', url: '/' },
            { name: 'Exclusão de dados', url: '/dados-exclusao' },
          ]}
        />
        <LegalPageShell
          eyebrow="LGPD"
          title="Solicitação concluída"
          subtitle="Seus dados cadastrais foram excluídos"
          showDivider={false}
        >
          <p>
            Concluímos a exclusão dos seus <strong>dados de cadastro</strong> (nome, CPF, telefone e e-mail
            de contato) e removemos seu endereço das nossas comunicações promocionais.
          </p>
          <p>
            O <strong>prontuário clínico</strong> da consulta permanece arquivado por{' '}
            <strong>obrigação legal do serviço de saúde</strong> (Lei nº 13.787/2018 — guarda mínima de 20 anos),
            protegido por <strong>sigilo médico</strong>. Esse registro não é usado para marketing nem contato
            promocional.
          </p>
          {protocol ? (
            <p>
              Protocolo: <code>{protocol}</code>
            </p>
          ) : null}
          <p style={{ marginBottom: 0 }}>
            Em caso de dúvida, escreva para o Encarregado / Diretor Técnico em{' '}
            <a href="mailto:atendimento@consultamais.org">atendimento@consultamais.org</a> informando este protocolo.
          </p>
        </LegalPageShell>
      </>
    );
  }

  return (
    <>
      <PageSeo
        path="/dados-exclusao"
        title="Exclusão de dados (LGPD) — Consulta+"
        description="Peça a exclusão dos seus dados cadastrais na Consulta+. Informe nome, e-mail, CPF e telefone usados na consulta."
        breadcrumbs={[
          { name: 'Início', url: '/' },
          { name: 'Exclusão de dados', url: '/dados-exclusao' },
        ]}
      />
      <LegalPageShell
        eyebrow="LGPD · Art. 18"
        title="Exclusão de dados cadastrais"
        subtitle="Informe os mesmos dados usados na sua consulta"
      >
        <p>
          Use esta página para solicitar a <strong>exclusão dos seus dados de cadastro</strong> (nome, CPF,
          telefone e e-mail) e o descadastro de comunicações promocionais.
        </p>
        <p>
          Por exigência legal, o <strong>prontuário clínico</strong> da consulta é mantido em guarda sigilosa
          pelo serviço de saúde — não é apagado por esta solicitação.
        </p>

        <form onSubmit={handleSubmit} noValidate>
          <label style={labelStyle}>
            Nome completo
            <input
              type="text"
              autoComplete="name"
              value={fullName}
              onChange={(ev) => setFullName(ev.target.value)}
              style={fieldStyle}
              required
            />
          </label>

          <label style={labelStyle}>
            E-mail
            <input
              type="email"
              autoComplete="email"
              inputMode="email"
              value={email}
              onChange={(ev) => setEmail(ev.target.value)}
              style={fieldStyle}
              required
            />
          </label>

          <label style={labelStyle}>
            CPF
            <input
              type="text"
              inputMode="numeric"
              autoComplete="off"
              value={cpf}
              onChange={(ev) => setCpf(formatCpf(ev.target.value))}
              style={fieldStyle}
              required
            />
          </label>

          <label style={labelStyle}>
            Telefone com DDD
            <input
              type="tel"
              autoComplete="tel"
              inputMode="numeric"
              value={phone}
              onChange={(ev) => setPhone(formatPhoneBR(ev.target.value))}
              style={fieldStyle}
              required
            />
          </label>

          {error ? (
            <p
              role="alert"
              style={{
                margin: '0.5rem 0 0',
                padding: '0.75rem 0.85rem',
                borderRadius: '0.65rem',
                background: '#FEF2F2',
                color: '#991B1B',
                fontSize: '0.88rem',
                lineHeight: 1.5,
              }}
            >
              {error}
            </p>
          ) : null}

          <button type="submit" disabled={!formValid || submitting} style={btnStyle(!formValid || submitting)}>
            {submitting ? 'Processando…' : 'Excluir meus dados cadastrais'}
          </button>
        </form>
      </LegalPageShell>
    </>
  );
}
